'use client'

import { useSession } from 'next-auth/react'
import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import { useEffect, useRef } from 'react'

export function PHProvider({ children }: { children: React.ReactNode }) {
  const { data: session, status } = useSession()
  const lastIdentifiedIdRef = useRef<string | null>(null)

  useEffect(() => {
    posthog.init('phc_gQf1AVs5zD7fYKYa4tZ2Ij9FJadSTbRUj0QFX0qO1ls', {
      api_host: 'https://us.i.posthog.com',
      person_profiles: 'identified_only',
      capture_pageview: false, // Disable automatic pageview capture on load, we'll do it manually
      loaded: (posthog) => {
        posthog.capture('$pageview')
      },

      // Enable autocapture - this will capture clicks, form submissions, and pageviews automatically.
      // capture_copied_text emits a `$copy_autocapture` event whenever a user copies/cuts text
      // (Cmd+C, right-click → Copy). We use this to track copies of the reviewer output; scope it
      // in PostHog by filtering `$copy_autocapture` on the review container (see data-attr="review-output").
      // Passing an object keeps the other autocapture defaults (click/change/submit) on.
      autocapture: {
        capture_copied_text: true,
      },

      // Strip the literal copied text from copy-autocapture events before they leave the browser.
      // The review panel can contain manuscript/review content, so we keep only a character count
      // (metadata) and never send `$selected_content` to PostHog.
      before_send: (event) => {
        // Drop $exception events originating from browser wallet extensions
        // (MetaMask, Phantom, etc.). Their errors are injected into every page
        // and swamp the real-app signal in the PostHog dashboard.
        if (event?.event === '$exception') {
          const stack = JSON.stringify(event.properties?.['$exception_list'] ?? '')
          if (/(chrome|moz|safari-web)-extension:\/\/|MetaMask|Phantom/i.test(stack)) return null
        }
        if (event && event.event === '$copy_autocapture' && event.properties) {
          const selected = event.properties['$selected_content']
          if (typeof selected === 'string') {
            event.properties['copied_char_count'] = selected.length
          }
          delete event.properties['$selected_content']
        }
        return event
      },

      // Enable session recording with privacy settings
      enable_recording_console_log: true,
      session_recording: {
        maskAllInputs: true, // Masks all input fields for privacy
      },

      // Optional: Configure specific autocapture settings
      capture_pageleave: true, // Capture when users leave pages

      // Enable automatic exception capture
      capture_exceptions: true,
    })

    // Capture uncaught errors
    const handleError = (event: ErrorEvent) => {
      posthog.captureException(event.error || new Error(event.message), {
        type: 'window_error',
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        url: window.location.href,
      })
    }

    // Capture unhandled promise rejections
    const handleRejection = (event: PromiseRejectionEvent) => {
      posthog.captureException(new Error(String(event.reason)), {
        type: 'unhandled_rejection',
        reason: String(event.reason),
        url: window.location.href,
      })
    }

    window.addEventListener('error', handleError)
    window.addEventListener('unhandledrejection', handleRejection)

    // Nudge-email click attribution. The three transactional emails land users
    // with utm_campaign identifying which nudge sent them. Fire once per page
    // load; downstream funnels attach to the same $pageview via distinct_id.
    // See docs/analytics/events.md.
    try {
      const qs = new URLSearchParams(window.location.search)
      const campaign = qs.get('utm_campaign')
      const NUDGE_CAMPAIGNS: Record<string, 'post_6h' | 'revise' | 'review_ready'> = {
        '6h_nudge': 'post_6h',
        revise_nudge: 'revise',
        review_ready: 'review_ready',
      }
      if (campaign && campaign in NUDGE_CAMPAIGNS) {
        const nudgeKind = NUDGE_CAMPAIGNS[campaign]
        const match = window.location.pathname.match(/^\/review\/([^/]+)/)
        posthog.capture('nudge_link_clicked', {
          nudgeKind,
          dest: window.location.pathname,
          reviewSessionId: match?.[1] ?? null,
        })
      }
    } catch {
      // URL parsing failure is not worth surfacing.
    }

    return () => {
      window.removeEventListener('error', handleError)
      window.removeEventListener('unhandledrejection', handleRejection)
    }
  }, [])

  // Identify the authenticated user and attach email/name
  const userId = (session?.user as { id?: string } | undefined)?.id
  const userEmail = session?.user?.email ?? undefined
  const userName = session?.user?.name ?? undefined
  useEffect(() => {
    if (status === 'authenticated') {
      const distinctId = userId || userEmail
      if (!distinctId) return

      if (lastIdentifiedIdRef.current !== distinctId) {
        const personProps: Record<string, string> = {}
        if (userEmail) personProps['$email'] = userEmail
        if (userName) personProps['$name'] = userName
        posthog.identify(distinctId, personProps)
        lastIdentifiedIdRef.current = distinctId
      }
    }

    if (status === 'unauthenticated') {
      lastIdentifiedIdRef.current = null
      posthog.reset()
    }
  }, [userId, userEmail, userName, status])

  return <PostHogProvider client={posthog}>{children}</PostHogProvider>
}
